/**
 * Date utility functions for recipe scheduling
 */

/**
 * Calculate the next occurrence of a specific day of the week
 * @param {number} dayOfWeek - Day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 * @param {Date} fromDate - The date to calculate from (defaults to today)
 * @returns {string} - Date string in YYYY-MM-DD format
 */
export const getNextOccurrence = (dayOfWeek, fromDate = new Date()) => {
  // Use UTC to avoid timezone issues
  const today = new Date(fromDate.getTime());
  const currentDay = today.getUTCDay();
  
  let daysUntilTarget;
  if (dayOfWeek === currentDay) {
    // If it's the same day, schedule for today
    daysUntilTarget = 0;
  } else if (dayOfWeek > currentDay) {
    // Target day is later this week
    daysUntilTarget = dayOfWeek - currentDay;
  } else {
    // Target day is next week
    daysUntilTarget = 7 - currentDay + dayOfWeek;
  }
  
  const targetDate = new Date(today.getTime());
  targetDate.setUTCDate(today.getUTCDate() + daysUntilTarget);
  
  // Format as YYYY-MM-DD for database storage
  return targetDate.toISOString().split('T')[0];
};

/**
 * Validate if a date is in the future (or today)
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} - True if date is today or in the future
 */
export const isValidFutureDate = (dateString) => {
  if (!dateString) return false;
  
  const selectedDate = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to compare dates only
  
  return selectedDate >= today;
};

/**
 * Format a date for display
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} - Formatted date string
 */
export const formatDateForDisplay = (dateString) => {
  // Add 'T12:00:00' to avoid timezone issues when parsing YYYY-MM-DD format
  const date = new Date(dateString + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
};